import React from "react";
import {
  Badge,
  Box,
  Flex,
  HStack,
  RadioGroup,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  DeliveryOption,
  EnrichedSupplierForDelivery,
  SelectedDeliveryOption,
} from "../../types";
import { ValidationError } from "../../validation/deliverySelectionValidation";
import { QuickEtaOption } from "../../hooks/useManualOptionHandlers";
import { CPTSlabForManualOption } from "../../../../views/Orders/enums";
import { DeliveryOptionsTableHeader } from "./DeliveryOptionsTableHeader";
import { DeliveryOptionRow } from "./DeliveryOptionRow";
import { ManualDeliveryOptionForm } from "./ManualDeliveryOptionForm";
import { RemainingItemsBanner } from "./RemainingItemsBanner";

export interface DeliveryOptionsPanelProps {
  supplier: EnrichedSupplierForDelivery;
  selection: SelectedDeliveryOption | undefined;
  estimateNo: string;
  isExpired: boolean;
  isRemainingItems: boolean;
  radioValue: string;
  isManualSelected: boolean;
  isDeliveryOptionSelected: (deliveryOptionId: string) => boolean;
  errorsByField: Record<string, ValidationError[] | undefined>;
  quickEtaOptions: QuickEtaOption[];
  manualSaveError: string | null;
  isSavingManualOption: boolean;
  isDeleteDisabled: boolean;
  isDeletingOption: boolean;
  onRadioChange: (value: string) => void;
  onDeliveryOptionSelect: (deliveryOptionId: string) => void;
  onManualOptionSelect: () => void;
  onOptionUpdate: (updatedOption: DeliveryOption) => void;
  onOptionDelete: (optionId: string) => Promise<void>;
  onManualCPTSlabChange: (val: CPTSlabForManualOption) => void;
  onManualCPTChange: (value: string) => void;
  onManualChargeChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onQuickEtaSelect: (minutes: number) => void;
  onManualEntryCancel: () => void;
  onManualEntrySave: () => void;
}

export const DeliveryOptionsPanel: React.FC<DeliveryOptionsPanelProps> = ({
  supplier,
  selection,
  estimateNo,
  isExpired,
  isRemainingItems,
  radioValue,
  isManualSelected,
  isDeliveryOptionSelected,
  errorsByField,
  quickEtaOptions,
  manualSaveError,
  isSavingManualOption,
  isDeleteDisabled,
  isDeletingOption,
  onRadioChange,
  onDeliveryOptionSelect,
  onManualOptionSelect,
  onOptionUpdate,
  onOptionDelete,
  onManualCPTSlabChange,
  onManualCPTChange,
  onManualChargeChange,
  onQuickEtaSelect,
  onManualEntryCancel,
  onManualEntrySave,
}) => (
  <Box flex="1.4" px={5} py={4}>
    <Flex justify="space-between" align="center" mb={4} pl={0}>
      <HStack spacing={1}>
        <Text
          fontSize="0.85rem"
          fontWeight="700"
          color="gray.700"
          fontFamily="'Nunito','Inter', sans-serif"
        >
          Delivery Options
        </Text>
        <Text color="red.500" fontWeight="bold">
          *
        </Text>
      </HStack>
      <Badge colorScheme="orange" variant="subtle" fontSize="0.65rem" mr={0}>
        SELECT ONE
      </Badge>
    </Flex>

    <RadioGroup
      value={radioValue}
      onChange={onRadioChange}
      isDisabled={isExpired || isRemainingItems}
    >
      <VStack align="stretch" spacing={2}>
        <DeliveryOptionsTableHeader showDeleteColumn={!isRemainingItems} />

        {supplier.deliveryOptions.map((option) => (
          <DeliveryOptionRow
            key={option._id}
            option={option}
            isSelected={isDeliveryOptionSelected(option._id)}
            isExpired={isExpired}
            isRemainingItems={isRemainingItems}
            estimateNo={estimateNo}
            splitId={supplier.splitId}
            onOptionUpdate={onOptionUpdate}
            onSelect={() => onDeliveryOptionSelect(option._id)}
            onDelete={onOptionDelete}
            isDeleteDisabled={isDeleteDisabled}
            isDeletingOption={isDeletingOption}
          />
        ))}

        <ManualDeliveryOptionForm
          isManualSelected={isManualSelected}
          isExpired={isExpired}
          isRemainingItems={isRemainingItems}
          selection={selection}
          quickEtaOptions={quickEtaOptions}
          errorsByField={errorsByField}
          manualSaveError={manualSaveError}
          isSavingManualOption={isSavingManualOption}
          onManualOptionSelect={onManualOptionSelect}
          onManualCPTSlabChange={onManualCPTSlabChange}
          onManualCPTChange={onManualCPTChange}
          onManualChargeChange={onManualChargeChange}
          onQuickEtaSelect={onQuickEtaSelect}
          onCancel={onManualEntryCancel}
          onSave={onManualEntrySave}
        />
      </VStack>
    </RadioGroup>

    {isRemainingItems && <RemainingItemsBanner />}
  </Box>
);
